import React, { useState, useEffect, useMemo, useRef } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';
import differenceInDays from 'date-fns/differenceInDays';
import _omit from 'lodash/omit';

import styles from './styles.module.scss';

import Button from '@components/_shared/button';
import { ListingPhotos } from '@components/_shared/listingPhotos';
import WysiwygText from '@components/_shared/wysiwygText';
import MetaHead from '@components/_shared/metaHead';
import ListingTitleAndFeatures from '@components/listing/listingTitleAndFeatures';
import AmenitiesList from '@components/_shared/amenityV2/amenitiesList';
import { VideoPlayer } from '@components/_shared/videoPlayer';
import { ImageSelector } from '@components/listing/ImagesSelector';
import { CancellationPolicy } from '@components/_shared/cancellationPolicy';
import { ListingPricing } from '@components/listing/listingPricing';
import { ListingReview } from '@components/listing/listingReview';
import { Neighborhood } from '@components/listing/neighborhood';
import { getListing } from '@api/listings/getListing';
import { getListingTitle, getYoutubeThumbnail } from '@utils/helpers';
import { withListing, withLayoutAndSession } from '@utils/hocs';
import { useToggleBodyScroll } from '@utils/hooks/toggleBodyScroll';
import { ListingType } from '@utils/types/listing';
import { LatLng } from '@utils/types/locations';
import {
  EstimateUpsellAlternativeType,
  ListingPriceEstimateType,
} from '@utils/types/estimate';
import { DatesRange } from '@utils/types/calendar';
import { ListingSlug } from '@utils/types/listingSlug';
import { useListing } from '@utils/context/listing';
import {
  SegmentEventNamesEnum,
  SegmentEventPagesEnum,
  useAnalytics,
} from '@utils/hooks/useAnalytics';
import { useSession } from '@utils/context/session';
import { getAllListingSlugs } from '@api/listings/getAllListingSlugs';
import CheckoutLinks from '@components/checkout/links';
import { RovePromise } from '@components/listing/rovePromise';
import SleepingSpaces from '@components/_shared/sleepingSpaces';
import { OverlayGalleryButtons } from '@components/listing/overlayGalleryButtons';
import { Cross } from '@assets/icons';
import ListingCard, {
  ListingInfoForCard,
} from '@components/collections/featuredListings/listingCard';
import { getCollectionBySlug } from '@api/collections/getCollectionBySlug';
import { searchListingsRequest } from '@api/listings/searchListingsRequest';

type Props = {
  listing: ListingType;
  featuredListings: ListingInfoForCard[];
};

function Listing(props: Props) {
  const { listing, featuredListings } = props;
  const router = useRouter();
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [expandedImages, setExpandedImages] = useState<boolean>(false);
  const [scrollToFloorplan, setScrollToFloorplan] = useState(false);
  const { disableScroll, enableScroll } = useToggleBodyScroll();
  const { setListingFieldsForBooking, resetStore } = useListing();
  const { user } = useSession();
  const { trackEvent } = useAnalytics();
  const reviewsRef = useRef<HTMLDivElement>(null);
  const videoTourRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  const openPriceModal = () => {
    disableScroll();
    setShowPriceModal(true);
  };

  const closePriceModal = () => {
    enableScroll();
    setShowPriceModal(false);
  };

  const scrollToReviews = () => {
    if (listing.listing_reviews.length && reviewsRef.current) {
      reviewsRef.current.scrollIntoView();
    }
  };

  const scrollToVideoTour = () => {
    if (videoTourRef.current) {
      videoTourRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const onViewOnMapClick = () => {
    if (mapRef.current) {
      mapRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const listingCoordinates: LatLng | null = useMemo(() => {
    if (listing && listing.address)
      return {
        lat: listing.address.coords.latitude,
        lng: listing.address.coords.longitude,
      };
    else return null;
  }, [listing]);

  const listingImages = useMemo(() => {
    const images = listing.photos.map((i) => i.url);
    if (listing.floorPlan?.asset?.url) {
      images.push(listing.floorPlan.asset.url);
    }
    return images;
  }, [listing.photos]);

  const isNYCProperty = useMemo(() => {
    return listing.collections?.includes('new-york-city');
  }, [listing.collections?.length]);

  const onFloorplanClick = () => {
    setScrollToFloorplan(true);
    setExpandedImages(true);
  };

  useEffect(() => {
    if (scrollToFloorplan) {
      setScrollToFloorplan(false);
    }
  }, [scrollToFloorplan]);

  useEffect(() => {
    if (!expandedImages) {
      window.scrollTo(0, 0);
    }
  }, [expandedImages]);

  useEffect(() => {
    resetStore();
    localStorage.removeItem('listingInfo');
    if (router.query.scrollto === 'reviews') scrollToReviews();
  }, []);

  const noIndex = useMemo(() => {
    return (
      listing.is_listed === false ||
      listing.is_active === false ||
      listing.is_cms_published === false
    );
  }, [listing.is_listed, listing.is_active, listing.is_cms_published]);

  const onBookNow = (
    data: ListingPriceEstimateType,
    dateRange: DatesRange,
    petFee: boolean = false,
    payMonthly?: boolean,
    upsellAlternative?: EstimateUpsellAlternativeType
  ) => {
    trackEvent(
      dateRange.to &&
        dateRange.from &&
        differenceInDays(dateRange.to, dateRange.from) > 30
        ? SegmentEventNamesEnum.REQUEST_TO_BOOK_CLICKED
        : SegmentEventNamesEnum.BOOK_NOW_CLICKED,
      {
        PageName: SegmentEventPagesEnum.LISTING_PAGE,
        ListingId: listing.id,
        ListingTitle: getListingTitle(listing),
      }
    );
    data.is_pet_fee = petFee;
    setListingFieldsForBooking({
      listing,
      estimate: data,
      dates: dateRange,
      withPetFee: petFee,
      paymentMethod: {
        paymentStructure: payMonthly ? 'monthly' : 'full',
        type: null,
        id: null,
      },
      upsellAlternative,
    });

    if (user) {
      router.push(CheckoutLinks.COMPLETE_PURCHASE);
    } else {
      router.push(CheckoutLinks.CREATE_ACCOUNT);
    }
  };

  const renderedListingPricing = useMemo(
    () => (
      <ListingPricing
        nightlyPrices={listing.nightly_prices}
        calendarMinNights={listing.calendar_min_nights}
        listingHasPetFee={!!listing.pet_fee}
        reservedDates={listing.availability_events.ranges}
        minStay={listing.min_stay}
        maxStay={listing.max_stay}
        weeklyPriceFactor={listing.weekly_price_factor}
        monthlyPriceFactor={listing.monthly_price_factor}
        firstAvailableDate={listing.first_available_date}
        lastKnownDay={listing.last_known_date}
        listingId={listing.id}
        onBookNowClick={onBookNow}
        averageMonthlyPrice={listing.monthly_average_price}
        sixtyDaysAveragePrice={listing.sixty_days_average_price}
        listingTitle={getListingTitle(listing)}
        containerStyle={styles.listingPriceContainer}
        isConversationEnabled={listing.is_conversation_enabled}
        highestPrice={listing.average_monthly_stay}
      />
    ),
    [listing]
  );

  const renderMetaHead = useMemo(() => {
    return (
      <MetaHead
        title={listing.metaTitle || getListingTitle(listing)}
        description={listing.metaDescription || ''}
        ogImage={listing.photos[0]?.url || ''}
        noIndex={noIndex}
        canonical={`https://www.rovetravel.com/listing/${listing.url_slug}`}
      />
    );
  }, [listing.metaTitle, listing.metaDescription, listing.photos, noIndex]);

  if (expandedImages) {
    return (
      <>
        {renderMetaHead}

        <div className={styles.container}>
          <div className={styles.listingContent}>
            <ListingPhotos
              images={listingImages}
              scrollToFloorplan={scrollToFloorplan}
              isExpanded
              toggleExpand={() => setExpandedImages(false)}
              listingTitle={getListingTitle(listing)}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {renderMetaHead}
      <div className={styles.container}>
        <div className={styles.listingContent}>
          <ListingTitleAndFeatures
            listing={listing}
            onViewOnMapClick={onViewOnMapClick}
            scrollToReviews={scrollToReviews}
          />

          {!!listingImages?.length && (
            <div className={styles.galleryContainer}>
              {(listing.videoTourUrl || listing.floorPlan) && (
                <OverlayGalleryButtons
                  scrollToVideoTour={scrollToVideoTour}
                  onFloorplanClick={onFloorplanClick}
                  videoTourUrl={listing.videoTourUrl}
                  floorPlan={listing.floorPlan?.asset?.url}
                />
              )}

              <ListingPhotos
                images={listingImages}
                isExpanded={false}
                toggleExpand={() => setExpandedImages(true)}
                listingTitle={getListingTitle(listing)}
              />
            </div>
          )}

          <div className={styles.overviewContainer}>
            <div className={styles.leftContainer}>
              <h2>Overview</h2>

              {listing.aboutRaw && (
                <WysiwygText
                  content={listing.aboutRaw}
                  containerStyle={styles.overviewBlocks__about}
                />
              )}

              <div className={styles.overviewBlocks__lightBlock}>
                <div className={styles.overviewBlocks__amenities}>
                  <h4 className={styles.overviewBlocks__subtitle}>
                    What this residence offers
                  </h4>

                  <AmenitiesList
                    items={listing.amenities}
                    listStyle={styles.amenitiesContainer}
                  />
                </div>

                {listing.homeTruthsRaw && (
                  <div className={styles.overviewBlocks__homeTruth}>
                    <h4 className={styles.overviewBlocks__subtitle}>
                      Home Truths
                    </h4>

                    <WysiwygText
                      content={listing.homeTruthsRaw}
                      containerStyle={styles.overviewBlocks__summary}
                    />
                  </div>
                )}
              </div>

              {listing.sleeping_arrangements?.length && (
                <div className={styles.overviewBlocks__darkBlock}>
                  <h4 className={styles.overviewBlocks__subtitle}>
                    Sleeping Spaces
                  </h4>

                  <div className={styles.overviewBlocks__summary}>
                    <p>A breakdown of the bedrooms in the property:</p>

                    <SleepingSpaces items={listing.sleeping_arrangements} />
                  </div>
                </div>
              )}
            </div>

            <div className={styles.rightDesktopContainer}>
              {renderedListingPricing}
            </div>
          </div>

          {listing.listingSpaces && (
            <div className={styles.imagesContainer}>
              <ImageSelector spaces={listing.listingSpaces} />
            </div>
          )}

          <RovePromise />

          {listing.videoTourUrl && (
            <div
              className={styles.videoContainer}
              ref={videoTourRef}
              id="videoTour"
            >
              {listing.videoTourUrl.includes('my.matterport.com') ? (
                <>
                  <iframe
                    src={listing.videoTourUrl}
                    allowFullScreen
                    allow="xr-spatial-tracking"
                    className={styles.video}
                  />
                </>
              ) : (
                <>
                  <VideoPlayer
                    src={listing.videoTourUrl}
                    containerStyle={styles.video}
                    thumbnail={getYoutubeThumbnail(listing.videoTourUrl)}
                  />
                </>
              )}
            </div>
          )}

          <div className={styles.rulesAndPolicy}>
            <CancellationPolicy
              cancellationPolicy={listing.cancellationPolicyRaw}
              containerStyle={styles.cancellationPolicy}
              withSecurityDepositPolicy={isNYCProperty}
              nycCancellationPolicy={isNYCProperty}
              securityDepositPolicy={listing.securityDepositPolicy}
            />
          </div>

          {listingCoordinates && (
            <div ref={mapRef} style={{ scrollMarginTop: '80px' }}>
              <Neighborhood
                listingLocation={listingCoordinates}
                containerStyle={styles.neighborhood}
              />
            </div>
          )}

          {!!listing.listing_reviews.length && (
            <div ref={reviewsRef} id="reviews">
              <ListingReview
                rating={+listing.rating}
                reviewAverages={listing.review_averages}
                reviewAveragesBySource={listing.review_averages_by_source}
                reviews={listing.listing_reviews}
                reviewsCount={listing.listing_reviews.length}
              />
            </div>
          )}

          {featuredListings.length > 0 && (
            <div className={styles.otherListingsContainer}>
              <span className={styles.title}>
                Renters also viewed these properties
              </span>
              <div className={styles.listingsContainer}>
                {featuredListings.map((l) => (
                  <ListingCard
                    listing={l}
                    key={`featured--${l.url_slug}`}
                    containerStyle={styles.listingsContainer__listing}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.priceMobileFooter}>
          <div className={styles.priceMobileFooter__buttons}>
            <Button
              containerStyle={styles.priceMobileFooter__button}
              onClick={openPriceModal}
              variant="blueContained"
            >
              Book
            </Button>
          </div>
        </div>

        {showPriceModal && (
          <div className={styles.priceMobileModal}>
            <div className={styles.priceModalContainer}>
              <div className={styles.header}>
                <Cross onClick={closePriceModal} />
              </div>
              {renderedListingPricing}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export const getStaticProps: GetStaticProps<
  {
    listing: Omit<
      ListingType,
      | 'public_description'
      | 'private_description'
      | 'units'
      | 'sleeping_areas'
      | 'state_changes'
      | 'listing_managers'
      | 'company'
      | 'images'
    >;
    featuredListings: ListingInfoForCard[];
  },
  { slug: string }
> = async (context) => {
  if (!context.params) {
    Sentry.captureException('Listing slug is required');
    throw Error('Listing slug is required');
  }

  try {
    const listing = await getListing(context.params.slug);
    const collectionCmsInfo = await getCollectionBySlug(
      listing.collections[0] as string
    );

    const featuredListings = [];
    if (!!collectionCmsInfo?.related_listings?.length) {
      const guestyIds = collectionCmsInfo.related_listings
        .filter((el) => !!el)
        .slice(0, 16)
        .map((el) => el.guestyId);
      const data = await searchListingsRequest({
        guestyIds,
      });
      featuredListings.push(
        ...data.listings
          .slice(0, 16)
          .map(
            (listing) =>
              _omit(listing, [
                'available_dates',
                'calendar_nightly_price',
                'valid_start_dates',
                'valid_end_dates',
                'calendar_min_nights',
              ]) as ListingInfoForCard
          )
      );
    }

    return {
      props: {
        listing: _omit(listing, [
          'public_description',
          'private_description',
          'units',
          'sleeping_areas',
          'state_changes',
          'listing_managers',
          'company',
          'images', // CMS images
        ]),
        featuredListings,
      },
      revalidate: 30,
    };
  } catch (error) {
    Sentry.captureException(error);
    return {
      notFound: true,
      revalidate: 10,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const listings = await getAllListingSlugs();
  return {
    paths: listings.map((l: ListingSlug) => {
      return {
        params: {
          slug: l.url_slug,
        },
      };
    }),
    fallback: 'blocking',
  };
};

export default withListing(
  withLayoutAndSession(Listing, {
    headerProps: { theme: 'white', noGlassEffect: true },
    footerProps: { theme: 'white' },
  })
);
